import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { toast } from "sonner";
import { Briefcase, MapPin, Clock, X } from "lucide-react";

export default function Jobs() {
  const { t, lang } = useI18n();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyingTo, setApplyingTo] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/jobs").then(({ data }) => { setJobs(data); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  return (
    <div data-testid="jobs-page">
      <section className="bg-[#0F172A] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
          <div className="label-mono text-[#FFB800]">Careers</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-4">{t("jobs.title")}</h1>
          <p className="mt-4 text-slate-300 max-w-2xl">{t("jobs.subtitle")}</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        {loading ? (
          <div className="text-slate-500 text-center py-20">{t("common.loading")}</div>
        ) : jobs.length === 0 ? (
          <div className="border border-dashed border-slate-300 py-20 text-center text-slate-500" data-testid="jobs-empty">{t("jobs.empty")}</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {jobs.map((j) => (
              <div key={j.id} className="border border-slate-200 p-6 flex flex-col gap-3 hover:border-slate-900 transition-colors" data-testid={`job-${j.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#8D1B3D] text-white flex items-center justify-center"><Briefcase className="w-5 h-5" /></div>
                  <div className="label-mono text-slate-500">{t(`jobs.trades.${j.trade}`) || j.trade}</div>
                </div>
                <div className="font-display font-black text-2xl tracking-tight">
                  {lang === "ar" ? j.title_ar : j.title_en}
                </div>
                <div className="text-sm text-slate-600 leading-relaxed line-clamp-3">
                  {lang === "ar" ? j.description_ar : j.description_en}
                </div>
                <div className="flex flex-wrap gap-4 text-xs label-mono text-slate-500 mt-2">
                  <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> {j.location}</span>
                  <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {j.experience_years}+ {t("jobs.years")}</span>
                  {j.salary_min && <span>QAR {j.salary_min}-{j.salary_max}</span>}
                </div>
                <button onClick={() => setApplyingTo(j)} className="btn-maroon mt-2 px-5 py-2.5 font-bold tracking-wide self-start" data-testid={`apply-${j.id}`}>
                  {t("jobs.apply")}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {applyingTo && <ApplyModal job={applyingTo} onClose={() => setApplyingTo(null)} />}
    </div>
  );
}

function ApplyModal({ job, onClose }) {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", years_experience: 0, cover_note: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/jobs/${job.id}/apply`, { ...form, years_experience: parseInt(form.years_experience || 0) });
      toast.success(t("jobs.appSuccess"));
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="apply-modal">
      <div className="bg-white max-w-lg w-full p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-start gap-4 mb-6">
          <div>
            <div className="label-mono text-slate-500">{t("jobs.applyTitle")}</div>
            <div className="font-display font-black text-2xl mt-1">{lang === "ar" ? job.title_ar : job.title_en}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <Field label={t("auth.name")} value={form.full_name} onChange={(v) => setForm({ ...form, full_name: v })} required testid="app-name" />
          <Field label={t("auth.email")} type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="app-email" />
          <Field label={t("auth.phone")} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required testid="app-phone" />
          <Field label={t("jobs.yearsExp")} type="number" value={form.years_experience} onChange={(v) => setForm({ ...form, years_experience: v })} required testid="app-years" />
          <Field label={t("jobs.coverNote")} value={form.cover_note} onChange={(v) => setForm({ ...form, cover_note: v })} textarea testid="app-cover" />
          <button type="submit" disabled={loading} className="btn-maroon w-full py-3 font-bold disabled:opacity-60" data-testid="app-submit">
            {loading ? t("checkout.processing") : t("jobs.submitApp")}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, type = "text", value, onChange, required, textarea, testid }) {
  return (
    <label className="block">
      <div className="label-mono text-slate-500 mb-1.5">{label}{required ? " *" : ""}</div>
      {textarea ? (
        <textarea value={value} required={required} onChange={(e) => onChange(e.target.value)} rows={3} className="w-full border border-slate-300 px-3 py-2.5 outline-none focus:border-[#8D1B3D] text-sm resize-none" data-testid={testid} />
      ) : (
        <input type={type} value={value} required={required} onChange={(e) => onChange(e.target.value)} className="w-full border border-slate-300 px-3 py-2.5 outline-none focus:border-[#8D1B3D] text-sm" data-testid={testid} />
      )}
    </label>
  );
}
